// @ts-strict-ignore
import { configureStore } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectFocusedAction,
  selectInitialWorkflowData,
  selectInitialWorkflowProps,
  selectStore,
} from 'flow/store/redux/selectors';
import { reducerMap, State } from '~/store';

/**
 * Builds a throwaway Redux store preloaded with the initial workflow snapshot, so the
 * "Existing" pane of a diff view renders against pre-change data.
 *
 * Call this once per split view and pass the store down — calling it in sibling
 * components would build a separate store for each.
 */
export function useInitialWorkflowStore() {
  const currentStoreState = useSelector(selectStore);
  const initialWorkflowData = useSelector(selectInitialWorkflowData);
  const initialWorkflowProps = useSelector(selectInitialWorkflowProps);
  const focusedAction = useSelector(selectFocusedAction);

  // The store's contents depend only on the snapshot, so that's the whole dep list.
  // Diff mode is read-only, so nothing dispatches into the workflow slices — the rest
  // of the state read above is stable and doesn't belong here.
  return useMemo(() => {
    const preloadedState = {
      ...currentStoreState,
      workflowEditor: {
        ...currentStoreState.workflowEditor,
        workflowData: initialWorkflowData,
        workflowProps: initialWorkflowProps,
      },
    } satisfies State;

    // Cast to avoid TS2589 (excessively deep type instantiation) from
    // configureStore typehinting with preloadedState's unbounded recursive type.
    return configureStore({
      reducer: reducerMap,
      preloadedState: preloadedState as never,
    });
  }, [initialWorkflowData, initialWorkflowProps, focusedAction]);
}
